import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * "Saved", said — one notice for every save that used to close silently
 * (client, 2026-10-15: "it isn't super clear when or what is saved or
 * completed … does it make sense to add a clear banner when someone saves or
 * completes something"). DESIGN-DNA Part 5: success is **explicit and
 * specific** — "Saved — Sugarbush Pant, 3 prices", never a silent return to a
 * list.
 *
 * - **Says what happened to what**, in words: the caller names the thing.
 * - **Under the top bar, never over the bottom one**, where the screen's
 *   primary action and Cancel live (§2.2-1).
 * - **Goes by itself** after a few seconds, a little longer for a longer
 *   message; pointing at it holds it; its × takes it away at once.
 * - **Read out** to a screen reader as a polite status.
 * - **For success only.** A failure stays where it happened, beside the
 *   control, in a Callout — a notice that leaves on its own is the wrong
 *   place for something that has to be acted on.
 */

export interface NoticeInput {
  /** What happened, to what: "Saved — Sugarbush Pant, 3 prices". */
  readonly title: string;
  /** A second line when one helps: "Labels are ready to print." */
  readonly detail?: string;
}

interface ShownNotice extends NoticeInput {
  readonly id: number;
}

const NoticeContext = createContext<(notice: NoticeInput) => void>(() => {});

/** The longest a notice stays unless pointed at. */
const MOST_MS = 9000;

export function noticeLifetimeMs(notice: NoticeInput): number {
  const words = `${notice.title} ${notice.detail ?? ''}`.trim().split(/\s+/).length;
  // About as long as it takes to read twice, never under four seconds.
  return Math.min(MOST_MS, Math.max(4000, words * 500));
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState<ShownNotice[]>([]);
  const next = useRef(1);

  const notify = useCallback((notice: NoticeInput) => {
    const id = next.current;
    next.current += 1;
    // Three at most: an older one makes way rather than stacking down the page.
    setShown((current) => [...current.slice(-2), { ...notice, id }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setShown((current) => current.filter((notice) => notice.id !== id));
  }, []);

  return (
    <NoticeContext.Provider value={notify}>
      {children}
      <div className="notices" aria-live="polite" aria-relevant="additions">
        {shown.map((notice) => (
          <NoticeItem key={notice.id} notice={notice} onDismiss={dismiss} />
        ))}
      </div>
    </NoticeContext.Provider>
  );
}

/** `onDismiss` is the provider's own, stable — so a re-render never restarts the timer. */
function NoticeItem({
  notice,
  onDismiss,
}: {
  notice: ShownNotice;
  onDismiss: (id: number) => void;
}) {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => onDismiss(notice.id), noticeLifetimeMs(notice));
    return () => clearTimeout(timer);
  }, [held, notice, onDismiss]);

  return (
    <div className="notice" onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}>
      <span className="notice__mark" aria-hidden="true">
        ✓
      </span>
      <div className="notice__text">
        <strong>{notice.title}</strong>
        {notice.detail !== undefined ? <span>{notice.detail}</span> : null}
      </div>
      <button
        type="button"
        className="notice__close"
        aria-label="Dismiss"
        onClick={() => onDismiss(notice.id)}
      >
        ×
      </button>
    </div>
  );
}

/** Say that something worked: `notify({ title: 'Saved — Sugarbush Pant' })`. */
export function useNotice(): (notice: NoticeInput) => void {
  return useContext(NoticeContext);
}
